import { LoginElementBuilderArgs, Renderers, WithRequired } from './contracts';
import { buildSignInPath } from './utils';
import { useAsync, useCheckOktaSession } from '../hooks';
import { useOktaAuthManager, useOktaAuthnAppContext } from '../contexts';
import { OktaAuth, Tokens } from '@okta/okta-auth-js';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import { Redirect } from 'react-router';
import { Security } from '@okta/okta-react';
import { getDefaultOktaClientConfiguration } from '@ssar-app/okta-authn-core';
export type SecuredElementProps = {
  children?: ReactNode;
  onAuthenticated: LoginElementBuilderArgs['onAuthenticated'];
  renderers: WithRequired<Partial<Renderers>, 'renderLoginElement'>;
};

const defaultRenderers: Renderers = {
  renderErrorElement(inputError: unknown): JSX.Element {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const error = inputError as any;
    console.error(error);
    const errorMessage =
      error instanceof Error
        ? error.message
        : error?.toString
        ? error.toString()
        : error;
    return (
      <div>
        <p>Error:</p>
        <p>{errorMessage}</p>
      </div>
    );
  },
  renderLoadingElement(...messages: ReactNode[]): JSX.Element {
    return (
      <div>
        <p>Loading ...</p>
        {(messages || []).map((a, index) => {
          return <p key={index}>{a}</p>;
        })}
      </div>
    );
  },
  renderLoginElement(args: LoginElementBuilderArgs): JSX.Element {
    throw new Error(
      `Login renderer is not provided for okta client: ${args.oktaClientConfiguration.name}(${args.oktaClientConfiguration.name})`,
    );
  },
  renderNotFoundElement(): JSX.Element {
    return (
      <div>
        <p>Error:</p>
        <p>Requested resource is not found</p>
      </div>
    );
  },
};

export const SecuredElement = (props: SecuredElementProps) => {
  const { children, onAuthenticated, renderers: renderersInput } = props;

  const renderers: Renderers = useMemo(() => {
    return {
      ...defaultRenderers,
      ...renderersInput,
    };
  }, [renderersInput]);

  const { oktaClientConfigurations, oktaClientCode, loginMountPath } =
    useOktaAuthnAppContext();

  const defaultOktaClientConfiguration = useMemo(() => {
    return (
      getDefaultOktaClientConfiguration(oktaClientConfigurations) ||
      oktaClientConfigurations[0]
    );
  }, [oktaClientConfigurations]);
  const oktaAuthManager = useOktaAuthManager();

  const oktaAuth = useMemo(() => {
    if (!oktaClientCode) {
      return null;
    }
    return oktaAuthManager.getForOktaClientCode(oktaClientCode);
  }, [oktaAuthManager, oktaClientCode]);

  const { restoreOriginalUri } = oktaAuthManager.config?.oktaAuthOptions || {};
  const onAuthRequired = oktaAuthManager.config?.onAuthRequired;

  const { status, error, sessionStateAndTokens } = useCheckOktaSession(
    oktaAuth,
    true,
  );

  const renderLoginRedirectElement = useCallback(
    (oktaCode?: string | null) => {
      const isDefaultOktaClientCode =
        oktaCode === defaultOktaClientConfiguration.code;
      const effectiveOktaClientCode = isDefaultOktaClientCode ? '' : oktaCode;

      return (
        <Redirect
          to={buildSignInPath(loginMountPath, effectiveOktaClientCode)}
        />
      );
    },
    [defaultOktaClientConfiguration.code, loginMountPath],
  );

  if (!restoreOriginalUri) {
    return renderers.renderErrorElement(
      new Error(
        'Invalid OktaAuthManagerConfig provided.  restoreOriginalUri is a required field',
      ),
    );
  }

  if (status !== 'completed') {
    return renderers.renderLoadingElement(
      'Checking for existing session, please wait...',
    );
  } else if (error) {
    return renderers.renderErrorElement(error);
  }

  return (
    <>
      {oktaAuth && (
        <Security
          oktaAuth={oktaAuth}
          onAuthRequired={onAuthRequired}
          restoreOriginalUri={restoreOriginalUri}
        >
          {!!sessionStateAndTokens?.exists && (
            <SecuredElementInternal
              onAuthenticated={onAuthenticated}
              oktaAuth={oktaAuth}
              tokens={sessionStateAndTokens?.tokens}
              renderers={renderers}
            >
              {children}
            </SecuredElementInternal>
          )}
          {!sessionStateAndTokens?.exists && (
            <>{renderLoginRedirectElement(oktaClientCode)}</>
          )}
        </Security>
      )}
      {oktaClientCode && !oktaAuth && renderers.renderNotFoundElement()}
      {!oktaClientCode && !oktaAuth && renderLoginRedirectElement()}
    </>
  );
};

const SecuredElementInternal: FC<{
  oktaAuth: OktaAuth;
  tokens?: Tokens;
  renderers: Renderers;
  onAuthenticated: LoginElementBuilderArgs['onAuthenticated'];
}> = (props) => {
  const { children, onAuthenticated, oktaAuth, tokens, renderers } = props;

  const fn = useCallback(() => {
    return onAuthenticated(oktaAuth, tokens);
  }, [oktaAuth, onAuthenticated, tokens]);

  const { status, error } = useAsync(fn, true);

  if (status !== 'completed') {
    return renderers.renderLoadingElement('Loading, please wait...');
  } else if (error) {
    return renderers.renderErrorElement(error);
  }

  return <>{children}</>;
};
